import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Bell,
  ArrowLeft,
  ArrowRight,
  Sparkles,
  CalendarDays,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { settingsApi } from "@/lib/api";
import { EarlyBirdBanner } from "@/components/HomeExtras";

/**
 * /updates — public detail page for all news-ticker notifications the admin
 * has added. Ticker items on the home page link here (with an anchor for
 * the specific item). Each entry is rendered as a full card with its own
 * optional "Take action" button pointing to the CMS-configured URL.
 */
const Updates = () => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const location = useLocation();

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const s = await settingsApi.get();
        if (!cancel) setSettings(s);
      } catch {
        // fail silent — page shows the empty-state below
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const items = useMemo(
    () => {
      const cms = (settings?.newsItems || []).filter((n) => n?.text && n.active !== false);
      if (cms.length) return cms;
      // Fallback — same defaults the ticker uses, so /updates is never empty
      // even before admin populates the CMS.
      return [
        { id: "d1", text: "Early-bird registration is open — save ₹1,000 on every package.", url: "/register", active: true },
        { id: "d2", text: "Abstract submissions accepted till 30 Nov 2026 — email assamapicon2026tinsukia@gmail.com", url: "mailto:assamapicon2026tinsukia@gmail.com", active: true },
        { id: "d3", text: "Conference on 18–20 Dec 2026 · Tinsukia Medical College, Assam.", url: "", active: true },
      ];
    },
    [settings],
  );

  // If the URL has a #hash, smooth-scroll to that item once loaded.
  useEffect(() => {
    if (!loading && location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(`update-${id}`);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 120);
      }
    }
  }, [loading, location.hash, items.length]);

  return (
    <div className="min-h-screen bg-white" data-testid="page-updates">
      <EarlyBirdBanner />
      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden bg-apicon-navy py-14 text-white md:py-20">
        <div className="pointer-events-none absolute inset-0 heritage-pattern opacity-20 invert" />
        <div className="pointer-events-none absolute -top-24 -left-24 h-[360px] w-[360px] rounded-full bg-apicon-gold/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -right-16 h-[380px] w-[380px] rounded-full bg-apicon-gold/10 blur-3xl" />
        <div className="relative mx-auto w-full max-w-5xl px-4 md:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-[0.16em] text-white/70 hover:text-white"
            data-testid="updates-back-home"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to home
          </Link>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-apicon-gold/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-apicon-gold sm:text-[11px]">
            <Bell className="h-3 w-3" /> Announcements
          </div>
          <h1 className="mt-4 font-display text-3xl font-bold leading-tight sm:text-5xl">
            The latest from{" "}
            <span className="italic text-apicon-gold">APICON 2026</span>
          </h1>
          <p className="mt-4 max-w-xl text-sm text-white/70 md:text-base">
            Every notification the organising secretariat has issued —
            registration deadlines, hotel blocks, abstract updates and more.
          </p>
        </div>
      </section>

      {/* ================= LIST ================= */}
      <section className="relative py-14 md:py-20">
        <div className="mx-auto w-full max-w-4xl px-4 md:px-8">
          {loading && (
            <div className="space-y-4" data-testid="updates-loading">
              {[0, 1, 2].map((k) => (
                <div
                  key={k}
                  className="h-28 w-full animate-pulse rounded-3xl bg-apicon-mist"
                />
              ))}
            </div>
          )}

          {!loading && items.length === 0 && (
            <div
              className="rounded-3xl border border-apicon-navy/10 bg-apicon-mist/40 p-10 text-center"
              data-testid="updates-empty"
            >
              <Sparkles className="mx-auto h-8 w-8 text-apicon-gold" />
              <h2 className="mt-4 font-display text-2xl font-bold text-apicon-navy">
                No announcements yet.
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                When the organising secretariat posts a new update, it will appear here first.
              </p>
              <Link
                to="/"
                className="mt-6 inline-flex h-11 items-center gap-2 rounded-full bg-apicon-navy px-5 text-sm font-semibold text-white hover:bg-[#082C5A]"
              >
                Return home <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          {!loading && items.length > 0 && (
            <div className="space-y-4" data-testid="updates-list">
              {items.map((n, idx) => {
                const id = n.id || `n${idx}`;
                const isInternal = n.url && n.url.startsWith("/");
                return (
                  <motion.article
                    key={id}
                    id={`update-${id}`}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.35, delay: idx * 0.03 }}
                    className="group relative overflow-hidden rounded-3xl border border-apicon-navy/10 bg-white p-5 shadow-sm transition hover:border-apicon-navy/30 hover:shadow-md sm:p-6 md:p-7"
                    data-testid={`update-item-${id}`}
                  >
                    <span className="pointer-events-none absolute inset-y-0 left-0 w-1 rounded-r-full bg-apicon-gold" />
                    <div className="flex items-start gap-3 pl-2 sm:gap-4 sm:pl-3">
                      <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-apicon-navy/5 text-apicon-navy">
                        <Bell className="h-4 w-4 text-apicon-gold" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-apicon-green">
                          <CalendarDays className="h-3 w-3" />
                          Announcement #{String(idx + 1).padStart(2, "0")}
                        </div>
                        <p className="mt-2 text-base leading-relaxed text-apicon-navy sm:text-lg">
                          {n.text}
                        </p>
                        {n.url && (
                          <div className="mt-4">
                            {isInternal ? (
                              <Link
                                to={n.url}
                                className="inline-flex items-center gap-1.5 rounded-full border border-apicon-navy/20 bg-white px-4 py-2 text-xs font-semibold text-apicon-navy transition hover:border-apicon-navy hover:bg-apicon-mist"
                                data-testid={`update-cta-${id}`}
                              >
                                Take action <ArrowRight className="h-3.5 w-3.5" />
                              </Link>
                            ) : (
                              <a
                                href={n.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 rounded-full border border-apicon-navy/20 bg-white px-4 py-2 text-xs font-semibold text-apicon-navy transition hover:border-apicon-navy hover:bg-apicon-mist"
                                data-testid={`update-cta-${id}`}
                              >
                                Learn more <ArrowRight className="h-3.5 w-3.5" />
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.article>
                );
              })}
            </div>
          )}

          {!loading && items.length > 0 && (
            <div className="mt-10 flex flex-col items-center gap-3 rounded-3xl bg-apicon-mist/40 p-6 text-center sm:mt-14 sm:flex-row sm:justify-between sm:text-left">
              <div>
                <div className="font-display text-lg font-semibold text-apicon-navy">
                  Ready to lock in your seat?
                </div>
                <div className="text-sm text-gray-600">
                  Early-bird pricing is only live until 30 Nov 2026.
                </div>
              </div>
              <Button
                asChild
                className="rounded-full bg-apicon-gold font-semibold text-apicon-navy hover:bg-[#ffb400]"
                data-testid="updates-register-cta"
              >
                <Link to="/register">Register now <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Updates;
